import Decimal from 'decimal.js'
import { z } from 'zod'
import { CurrencySchema } from '@shared/contracts/enums'
import type { Currency } from '@shared/contracts/enums'

const LEDGER_DECIMAL_PLACES = 3

Decimal.set({ rounding: Decimal.ROUND_HALF_UP })

export const MonetaryValueSchema = z.object({
  amount: z.string().regex(/^-?\d+(\.\d{1,3})?$/),
  currency: CurrencySchema,
})

export type MonetaryValue = z.infer<typeof MonetaryValueSchema>

export class Money {
  private readonly value: Decimal
  readonly currency: Currency

  constructor(amount: string | number | Decimal, currency: Currency) {
    this.value = new Decimal(amount)
    this.currency = currency
  }

  static fromMonetaryValue(value: MonetaryValue): Money {
    return new Money(value.amount, value.currency)
  }

  add(other: Money): Money {
    this.assertSameCurrency(other)
    return new Money(this.value.plus(other.value), this.currency)
  }

  subtract(other: Money): Money {
    this.assertSameCurrency(other)
    return new Money(this.value.minus(other.value), this.currency)
  }

  percent(percentage: string | number | Decimal): Money {
    return new Money(
      this.value.mul(new Decimal(percentage).div(100)).toDecimalPlaces(LEDGER_DECIMAL_PLACES),
      this.currency,
    )
  }

  isNegative(): boolean {
    return this.value.isNegative()
  }

  isZero(): boolean {
    return this.value.isZero()
  }

  toNumber(): number {
    return this.value.toNumber()
  }

  toAmountString(): string {
    return this.value.toFixed(LEDGER_DECIMAL_PLACES)
  }

  toMonetaryValue(): MonetaryValue {
    return { amount: this.value.toFixed(LEDGER_DECIMAL_PLACES), currency: this.currency }
  }

  private assertSameCurrency(other: Money): void {
    if (other.currency !== this.currency) {
      throw new Error(`Moedas incompatíveis: ${this.currency} e ${other.currency}.`)
    }
  }
}
